<script lang="ts">
  import { getContext } from 'svelte'
  import { ViewMembershipInfo, ViewChange, ViewOrders } from './'
  import { getMemberships, getDefaultPaymentMethod, getMembershipByEmail } from '../api'
  import type { Membership, MembershipsPayload, MembershipByEmailPayload, ReadableQuery } from '../api'
  import Load from './Load.svelte'

  import { splitIo } from '@stores'
  import { Split, Treatment } from '@config'

  import { ordersAwaitRefetch, selectedProduct, loadingChange, loadingCancel, defaultPaymentMethod } from './store'
  import { Tabs, tab, TAB, Notification, NOTIFICATION_TYPE } from '../elements'
  import { profileContextKey } from './context'
  import ViewChangeRenewalPlan from './ViewChange/ViewChangeRenewalPlan.svelte'

  export let inventories
  export let userCountry
  export let email = ''

  let notification
  let showNotification = false
  let notificationTimeoutHandler
  let changeRenewalPlan
  let hasMembershipError = false
  let membershipTreatment: Treatment
  let enthusiastTreatment: Treatment
  let activeMembershipQuery: ReadableQuery<MembershipsPayload | MembershipByEmailPayload>
  let membership: Membership
  let membershipsQuery: ReadableQuery<MembershipsPayload>
  let membershipByEmailQuery: ReadableQuery<MembershipByEmailPayload>
  let isDuckCreekSource = false
  let hdcId = ''
  let sourceSystem = ''

  membershipTreatment = splitIo.getTreatment(Split.FeaturePreventDuplicateMemberships)
  enthusiastTreatment = splitIo.getTreatment(Split.FeatureEnthusiastHdc)
  const hasEnthusiastFeature = enthusiastTreatment === Treatment.On

  const useMemberGraphData =
    membershipTreatment === Treatment.MemberGraph || membershipTreatment === Treatment.BothMemberGraph
  const NOTIFICATION_TIMEOUT = process.env.NOTIFICATION_TIMEOUT as number
  const userId = getContext(profileContextKey).getUserId()

  switch (membershipTreatment) {
    case Treatment.Omni:
      membershipsQuery = getMemberships(userId)
      break
    case Treatment.MemberGraph:
      membershipByEmailQuery = getMembershipByEmail(email)
      break
    case Treatment.BothMemberGraph:
    case Treatment.BothOmni:
      membershipsQuery = getMemberships(userId)
      membershipByEmailQuery = getMembershipByEmail(email)
      break
    default:
      membershipsQuery = getMemberships(userId)
  }

  const defaultPaymentMethodQuery = getDefaultPaymentMethod(userId)
  activeMembershipQuery = useMemberGraphData ? membershipByEmailQuery : membershipsQuery

  const handleInventoryId = (membership) => {
    if ($loadingChange && (membership.inventoryId === $selectedProduct.id || $selectedProduct.isVip)) {
      notificationTimeoutHandler && clearTimeout(notificationTimeoutHandler)
      showNotification = true
      notificationTimeoutHandler = setTimeout(() => {
        showNotification = false
      }, NOTIFICATION_TIMEOUT)
      activeMembershipQuery.stopPolling()
      ordersAwaitRefetch.set(true)
      loadingChange.set(false)
    }
  }

  const normalizeMembershipData = (data, sourceSystem) => {
    const normalizedData = {
      currency: data.product.price.currency,
      effectiveAt: data.effectiveAt,
      expireAt: data.expireAt,
      id: data.omniMembershipId,
      inventoryId: data.product.omniInventoryId,
      level: data.product.level,
      orderId: data.omniOrderId,
      price: data.product.price.amount,
      productId: data.product.id,
      name: data.product.name,
      skuCode: data.product.skuCode,
      status: data.status.toLowerCase(),
      subscription: null,
      userId,
    }

    if (data.subscriptionStatus) {
      normalizedData.subscription = {}
      normalizedData.subscription.status = data.subscriptionStatus
      normalizedData.subscription.context = {}
      normalizedData.subscription.context.inventoryId = data.product.omniInventoryId
    }

    return normalizedData
  }

  const handleMembershipQuery = (): Membership => {
    const { error, data } = $membershipsQuery

    if (!error && data?.memberships?.data.length > 0) {
      const membership = data.memberships.data[0]
      handleInventoryId(membership)
      return membership
    } else {
      activeMembershipQuery.stopPolling()
      loadingCancel.set(false)
    }
    return undefined
  }

  const handleMembershipByEmailQuery = (): Membership => {
    const { error, data } = $membershipByEmailQuery
    const byEmail = data?.member?.byEmail

    sourceSystem = byEmail?.sourceSystem
    isDuckCreekSource = byEmail?.sourceSystem === 'DUCKCREEK'
    hdcId = byEmail?.id

    if (!error && byEmail?.assets?.length > 0) {
      const membership = normalizeMembershipData(byEmail.assets[0])
      handleInventoryId(membership)
      return membership
    } else if (byEmail?.errors?.length > 0) {
      hasMembershipError = true
    } else {
      activeMembershipQuery.stopPolling()
      loadingCancel.set(false)
    }
    return undefined
  }

  $: if (!$activeMembershipQuery.loading) {
    membership = useMemberGraphData ? handleMembershipByEmailQuery() : handleMembershipQuery()
  }

  $: defaultPaymentMethod.set($defaultPaymentMethodQuery.data?.defaultPaymentMethod?.data)
</script>

<Load query={activeMembershipQuery} hasError={hasMembershipError} name="membership">
  <Tabs {isDuckCreekSource} {hasEnthusiastFeature} on:click={() => (changeRenewalPlan = false)} />
  {#if $tab === TAB.INFO && showNotification}
    <Notification
      {notification}
      on:click={() => {
        notificationTimeoutHandler && clearTimeout(notificationTimeoutHandler)
        showNotification = false
      }}
    />
  {/if}
  <div class="panel" data-cy={`panel-${$tab}`}>
    {#if $tab === TAB.INFO}
      {#if changeRenewalPlan}
        <ViewChangeRenewalPlan
          {membership}
          {inventories}
          {activeMembershipQuery}
          {defaultPaymentMethodQuery}
          {userCountry}
          on:cancel={() => (changeRenewalPlan = false)}
          on:confirm={() => {
            changeRenewalPlan = false
            notification = NOTIFICATION_TYPE.RENEWAL_PLAN_CHANGED
            showNotification = true
            notificationTimeoutHandler && clearTimeout(notificationTimeoutHandler)
            notificationTimeoutHandler = setTimeout(() => {
              showNotification = false
            }, NOTIFICATION_TIMEOUT)
          }}
        />
      {:else}
        <ViewMembershipInfo
          {isDuckCreekSource}
          {hasEnthusiastFeature}
          {membership}
          {userCountry}
          {hdcId}
          {sourceSystem}
          on:changeRenewalPlan={() => (changeRenewalPlan = true)}
        />
      {/if}
    {:else if $tab === TAB.CHANGE}
      <ViewChange
        {membership}
        {inventories}
        {activeMembershipQuery}
        {userCountry}
        on:notify={({ detail: { isVip } }) => {
          notification = isVip ? NOTIFICATION_TYPE.VIP_REDEEMED : NOTIFICATION_TYPE.MEMBERSHIP_CHANGED
        }}
      />
    {:else if $tab === TAB.ORDERS}
      <ViewOrders {userCountry} />
    {/if}
  </div>
</Load>

<style>
  .panel {
    padding: 24px 16px 16px;
  }
</style>
